/**
 * AI Readiness Lead Magnet Section
 *
 * Shared marketing section used on the dedicated AI readiness page and
 * in enterprise pricing surfaces.
 *
 * Primary responsibilities:
 * - Present the AI readiness value proposition clearly
 * - Pair the public form with supporting trust and process copy
 * - Support compact and full-page marketing placements
 *
 * Dependencies:
 * - AI readiness lead-magnet form
 */

'use client'

import { CheckCircledIcon, LightningBoltIcon, ReaderIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import { AiReadinessLeadMagnetForm } from '@/components/quick-ai-readiness/lead-magnet-form'
import { getMarketingCopy } from '@/lib/marketing-copy'
import { AiReadinessLeadMagnetSource } from '@/lib/quick-ai-readiness/lead-magnet'
import { cn } from '@/lib/utils'

interface AiReadinessLeadMagnetSectionProps {
  source: AiReadinessLeadMagnetSource
  variant?: 'compact' | 'page'
  className?: string
}

const valueIcons = [ReaderIcon, LightningBoltIcon, CheckCircledIcon] as const
const accentIconClassName =
  'bg-[rgb(236,240,247)] text-[rgb(60,82,110)] dark:bg-sky-500/15 dark:text-sky-200'

export function AiReadinessLeadMagnetSection({
  source,
  variant = 'compact',
  className
}: AiReadinessLeadMagnetSectionProps) {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const isPageVariant = variant === 'page'
  const compactValueCards = copy.leadMagnet.valueCards.slice(0, 2)

  return (
    <section
      className={cn(
        'rounded-[2.5rem] border border-slate-200 bg-[linear-gradient(140deg,#f7f6f3_0%,#ffffff_46%,#eef2f8_100%)] shadow-[0_28px_100px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[linear-gradient(140deg,rgba(8,12,18,0.98)_0%,rgba(10,16,24,0.98)_46%,rgba(17,25,36,0.92)_100%)] dark:shadow-[0_28px_100px_rgba(0,0,0,0.34)]',
        className
      )}
    >
      <div
        className={cn(
          'grid gap-0 lg:items-start',
          isPageVariant ? 'xl:grid-cols-[0.78fr_1.22fr]' : 'lg:grid-cols-[0.95fr_1.05fr]'
        )}
      >
        <div
          className={cn(
            'relative overflow-hidden border-b border-slate-200 p-6 sm:p-8 dark:border-white/10',
            isPageVariant
              ? 'xl:border-b-0 xl:border-r xl:p-12'
              : 'lg:border-b-0 lg:border-r lg:p-10 xl:p-12'
          )}
        >
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent" />
          <div className="absolute right-[-10%] top-[-12%] size-56 rounded-full bg-sky-200/35 blur-3xl dark:bg-sky-500/10" />
          <div className="absolute bottom-[-18%] left-[-10%] size-64 rounded-full bg-stone-200/40 blur-3xl dark:bg-white/4" />

          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-600 dark:text-slate-300">
              {copy.leadMagnet.label}
            </p>
            <h2 className="font-display mt-4 max-w-[12ch] text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.leadMagnet.title}
            </h2>
            <p className="mt-5 max-w-[58ch] text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.leadMagnet.description}
            </p>

            {!isPageVariant ? (
              <div className="mt-8 grid gap-3">
                {compactValueCards.map((card, index) => {
                  const Icon = valueIcons[index] || valueIcons[0]

                  return (
                    <div
                      key={card.title}
                      className="bg-white/72 flex items-center gap-3 rounded-[1.35rem] border border-slate-200/80 px-4 py-3 dark:border-white/10 dark:bg-white/5"
                    >
                      <div
                        className={`flex size-10 shrink-0 items-center justify-center rounded-2xl ${accentIconClassName}`}
                      >
                        <Icon className="size-5" />
                      </div>
                      <p className="text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
                        {card.title}
                      </p>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className={cn('p-4 sm:p-6', isPageVariant ? 'xl:p-8' : 'lg:p-8')}>
          <AiReadinessLeadMagnetForm
            source={source}
            variant={isPageVariant ? 'page' : 'compact'}
            className={cn(isPageVariant ? 'xl:sticky xl:top-28' : 'lg:sticky lg:top-24')}
          />
        </div>
      </div>
    </section>
  )
}
